import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, App, Button, Card, Col, DatePicker, Empty, Form, Grid, Input, InputNumber, List, Modal, Popconfirm, Progress,
  Row, Segmented, Select, Space, Statistic, Table, Tag, Tooltip, Typography, theme,
} from 'antd';
import {
  CheckOutlined, DeleteOutlined, EditOutlined, LeftOutlined, PlusOutlined, ReloadOutlined, RightOutlined, TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip as GrafikIpucu, XAxis, YAxis,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { enhanceColumns, toplamGenislik } from '../ortak/tableHelpers';
import { IlIlceAlanlari, aramali } from './formParcalari';
import { LokasyonLinki, PLAN_DURUM_RENK, gunAdi, haftaEtiketi, hata, para, pazartesi, tarih } from './ortak';

/**
 * S3 Keşif Haftalık Plan (26.10.2026) — /satis-sureci/haftalik, /plan, /ekip.
 * Hedef KİŞİ BAŞI (satis_kesif_ekip.haftalik_hedef, varsayılan 3) yeni YS sözleşmesi; sözleşme lokasyon
 * sorumlusuna yazılır, hafta = sisteme giriş (eklenmeTarihi) haftası. Plan satırı: gün, bölge, hedef lokasyon,
 * not, durum. Ekip yönetimi yalnız Satış Süreci w; öneri listesi otomatik eklenmez.
 */
const { Text, Title } = Typography;

// dataviz referans paleti, kategorik 1. yuva (tek seri): açık / koyu yüzey için ayrı adım
const SERI = { acik: '#2a78d6', koyu: '#3987e5' };

function KpiKarti({ baslik, deger, alt, children }) {
  return (
    <Card size="small" style={{ height: '100%' }}>
      <Statistic title={baslik} value={deger} />
      {children}
      {alt ? <Text type="secondary" style={{ fontSize: 12 }}>{alt}</Text> : null}
    </Card>
  );
}

function Trend({ trend, darkMode }) {
  const { token } = theme.useToken();
  const ekipVar = trend.kisiler.some((k) => k.uye);
  const seri = ekipVar ? trend.ekip_toplam : trend.toplam;
  const veri = trend.haftalar.map((h, i) => ({ hafta: dayjs(h).format('DD.MM'), etiket: haftaEtiketi(h), deger: seri[i], gorusme: trend.gorusme[i] }));
  const renk = darkMode ? SERI.koyu : SERI.acik;
  const Ipucu = ({ active, payload }) => (active && payload?.length ? (
    <div style={{ background: token.colorBgElevated, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 6,
      padding: '6px 10px', boxShadow: token.boxShadowSecondary, color: token.colorText, fontSize: 12 }}>
      <div style={{ fontWeight: 600 }}>{payload[0].payload.etiket}</div>
      <div>{payload[0].value} yeni YS sözleşmesi</div>
      <div style={{ color: token.colorTextSecondary }}>{payload[0].payload.gorusme} görüşme kaydı</div>
    </div>) : null);
  return (
    <div>
      <Text strong>Son {trend.haftalar.length} hafta — {ekipVar ? 'ekip üyelerinin' : 'tüm'} yeni YS sözleşmesi</Text>
      {ekipVar && trend.ekip_hedef ? <Text type="secondary"> · kesikli çizgi: ekip hedefi ({trend.ekip_hedef}/hafta)</Text> : null}
      <div style={{ width: '100%', height: 220, marginTop: 8 }}>
        <ResponsiveContainer>
          <BarChart data={veri} margin={{ top: 16, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={token.colorBorderSecondary} />
            <XAxis dataKey="hafta" tick={{ fill: token.colorTextSecondary, fontSize: 12 }} axisLine={{ stroke: token.colorBorder }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fill: token.colorTextSecondary, fontSize: 12 }} axisLine={false} tickLine={false} />
            <GrafikIpucu content={<Ipucu />} cursor={{ fill: token.colorFillTertiary }} />
            {ekipVar && trend.ekip_hedef ? (
              <ReferenceLine y={trend.ekip_hedef} stroke={token.colorTextTertiary} strokeDasharray="4 4"
                label={{ value: `Hedef ${trend.ekip_hedef}`, position: 'insideTopRight', fill: token.colorTextSecondary, fontSize: 11 }} />
            ) : null}
            <Bar dataKey="deger" fill={renk} barSize={22} radius={[4, 4, 0, 0]} isAnimationActive={false}
              label={{ position: 'top', fill: token.colorTextSecondary, fontSize: 11 }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PlanFormu({ api, secenek, erisim, hafta, kayit, onKapat, onKaydedildi }) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const genis = Grid.useBreakpoint().md;
  const [gorusmeler, setGorusmeler] = useState([]);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const kisi = Form.useWatch('kullanici_no', form);
  const bas = dayjs(hafta);

  useEffect(() => {
    form.setFieldsValue(kayit ? { ...kayit, gun: dayjs(kayit.gun) } : {
      kullanici_no: erisim.uye ? erisim.kullanici_no : undefined,
      gun: dayjs().isBefore(bas) || dayjs().isAfter(bas.add(6, 'day'), 'day') ? bas : dayjs().startOf('day'), durum: 0,
    });
  }, [kayit, form]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!kisi) return;
    api.get('/gorusme', { bas: bas.format('YYYY-MM-DD'), bit: bas.add(6, 'day').format('YYYY-MM-DD'), gorusen: kisi })
      .then((d) => setGorusmeler(d.satirlar.map((g) => ({ value: g.id, label: `#${g.id} · ${g.lokasyon_adi} (${tarih(g.gorusme_tarihi)})` }))))
      .catch(() => setGorusmeler([]));
  }, [kisi, hafta]); // eslint-disable-line react-hooks/exhaustive-deps

  const kaydet = async () => {
    const v = await form.validateFields();
    const govde = Object.fromEntries(Object.entries({ ...v, gun: v.gun.format('YYYY-MM-DD') })
      .map(([k, x]) => [k, x === undefined || x === '' ? null : x]));
    setKaydediliyor(true);
    try {
      const p = kayit ? await api.put(`/plan/${kayit.id}`, govde) : await api.post('/plan', govde);
      message.success('Plan kaydedildi.');
      onKaydedildi(p);
      onKapat();
    } catch (e) {
      message.error(hata(e));
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <Modal open title={kayit ? 'Plan satırını düzenle' : `Plan ekle · ${haftaEtiketi(hafta)}`} onCancel={onKapat}
      onOk={kaydet} confirmLoading={kaydediliyor} okText="Kaydet" cancelText="Vazgeç" width={genis ? 640 : '100%'}
      style={genis ? undefined : { top: 0, maxWidth: '100vw', padding: 0 }}>
      <Form form={form} layout="vertical" requiredMark="optional">
        <Row gutter={12}>
          <Col xs={24} sm={14}>
            <Form.Item name="kullanici_no" label="Kişi" rules={[{ required: true, message: 'Kişi seçin' }]}>
              <Select {...aramali} allowClear={false} options={secenek.ekip} disabled={!erisim.yonetici}
                placeholder={secenek.ekip.length ? 'Ekip üyesi' : 'Önce ekibe kişi ekleyin'} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={10}>
            <Form.Item name="gun" label="Gün" rules={[{ required: true }]}>
              <DatePicker format="dd DD.MM.YYYY" style={{ width: '100%' }} inputReadOnly allowClear={false}
                disabledDate={(d) => d.isBefore(bas, 'day') || d.isAfter(bas.add(6, 'day'), 'day')} />
            </Form.Item>
          </Col>
        </Row>
        <IlIlceAlanlari form={form} api={api} iller={secenek.iller} ilAlan="il" ilceAlan="ilce" />
        <Form.Item name="bolge" label="Bölge / güzergâh"><Input maxLength={120} placeholder="Ör. Nilüfer – Görükle hattı" /></Form.Item>
        <Form.Item name="hedef_lokasyon" label="Hedef lokasyon"><Input maxLength={250} /></Form.Item>
        <Form.Item name="plan_notu" label="Not"><Input.TextArea rows={2} maxLength={2000} /></Form.Item>
        <Form.Item name="durum" label="Durum"><Segmented block options={secenek.plan_durumlari} /></Form.Item>
        <Form.Item name="gorusme_id" label="Bağlı görüşme (yapıldıysa)">
          <Select allowClear options={gorusmeler} placeholder={gorusmeler.length ? 'Bu haftaki görüşmelerinden' : 'Bu hafta görüşme yok'} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function EkipYonetimi({ api, secenek, onKapat, onDegisti }) {
  const { message } = App.useApp();
  const [veri, setVeri] = useState(null);
  const [kisi, setKisi] = useState(null);
  const [hedef, setHedef] = useState(secenek.varsayilan_hedef);
  const yukle = useCallback(() => api.get('/ekip').then(setVeri).catch((e) => message.error(hata(e))), [api, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const islem = (p) => p.then(() => { yukle(); onDegisti(); }).catch((e) => message.error(hata(e)));
  const ekle = (k, h = secenek.varsayilan_hedef) => islem(api.post('/ekip', { kullanici_no: k, haftalik_hedef: h }));

  return (
    <Modal open title={<Space><TeamOutlined />Keşif ekibi</Space>} onCancel={onKapat} footer={null} width={720}>
      <Table rowKey="kullanici_no" size="small" loading={!veri} dataSource={veri?.uyeler || []} pagination={false}
        locale={{ emptyText: 'Ekipte kimse yok — öneri listesinden ya da aşağıdan ekleyin.' }}
        columns={[
          { title: 'Kişi', dataIndex: 'ad_soyad', render: (v, u) => (
            // 27.10: işten ayrılmış (kullanıcı pasif) üye hedefe sayılmaz; buradan çıkarılabilir
            <Space size={4}>{v}{u.kullanici_aktif === 0 ? <Tooltip title="Kullanıcı pasif: haftalık hedefe ve plana girmez."><Tag color="error">pasif kullanıcı</Tag></Tooltip> : null}</Space>) },
          { title: 'Pozisyon', dataIndex: 'pozisyon_adi', render: (v) => v || <Text type="secondary">pozisyonsuz</Text> },
          { title: 'Haftalık hedef', dataIndex: 'haftalik_hedef', width: 130, render: (v, u) => (
            <InputNumber size="small" min={0} max={50} defaultValue={v} style={{ width: 80 }}
              onBlur={(e) => { const n = Number(e.target.value); if (n !== v && n >= 0) islem(api.put(`/ekip/${u.kullanici_no}`, { haftalik_hedef: n })); }} />) },
          { title: '', key: 'x', width: 60, render: (_, u) => (
            <Popconfirm title="Ekipten çıkarılsın mı?" okText="Çıkar" cancelText="Vazgeç" onConfirm={() => islem(api.del(`/ekip/${u.kullanici_no}`))}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Çıkar" />
            </Popconfirm>) },
        ]} />
      <Space wrap style={{ marginTop: 12 }}>
        <Select {...aramali} placeholder="Kişi ekle" value={kisi} onChange={setKisi} options={secenek.kullanicilar} style={{ width: 240 }} />
        <InputNumber min={0} max={50} value={hedef} onChange={setHedef} suffix="/hafta" style={{ width: 120 }} />
        <Button icon={<UserAddOutlined />} disabled={!kisi} onClick={() => { ekle(kisi, hedef ?? secenek.varsayilan_hedef); setKisi(null); }}>Ekle</Button>
      </Space>
      {veri?.oneriler?.length ? (
        <>
          <Title level={5} style={{ marginTop: 16 }}>Öneriler</Title>
          <Text type="secondary">Pozisyonu 104 / 105 / 107 / 66 / 81 olanlar ve pozisyonu boş yeni saha personeli — yalnız öneri, otomatik eklenmez.</Text>
          <List size="small" dataSource={veri.oneriler} style={{ marginTop: 8 }} renderItem={(o) => (
            <List.Item actions={[<Button key="e" size="small" icon={<PlusOutlined />} onClick={() => ekle(o.kullanici_no)}>Ekle</Button>]}>
              <List.Item.Meta title={o.ad_soyad} description={o.sema_pozisyon_adi || o.pozisyon_adi || 'pozisyonsuz'} />
            </List.Item>
          )} />
        </>
      ) : null}
    </Modal>
  );
}

export default function HaftalikPlan({ api, erisim, secenek, onEkipDegisti }) {
  const { message } = App.useApp();
  const { darkMode } = useTheme() || {};
  const mobil = !Grid.useBreakpoint().md;
  const [hafta, setHafta] = useState(() => pazartesi(dayjs()).format('YYYY-MM-DD'));
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [plan, setPlan] = useState(null); // {kayit}
  const [ekipAcik, setEkipAcik] = useState(false);

  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/haftalik', { hafta }).then(setVeri).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, hafta, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const kaydir = (n) => setHafta(dayjs(hafta).add(7 * n, 'day').format('YYYY-MM-DD'));
  const planYazar = (p) => erisim.yonetici || [p.kullanici_no, p.ekleyen].includes(erisim.kullanici_no);
  const planSil = (p) => api.del(`/plan/${p.id}`).then(() => { message.success('Plan satırı silindi.'); yukle(); })
    .catch((e) => message.error(hata(e)));
  const t = veri?.ekip_toplam;

  const kisiKolonlari = useMemo(() => enhanceColumns([
    { title: 'Kişi', dataIndex: 'ad_soyad', width: 200, render: (v, k) => (
      <Space size={4}>{v}{!k.uye ? <Tag>ekip dışı</Tag> : null}{k.kullanici_no === erisim.kullanici_no ? <Tag color="blue">siz</Tag> : null}</Space>) },
    { title: 'Hedef', dataIndex: 'hedef', width: 80, align: 'right', render: (v) => v ?? '–' },
    { title: 'Yeni YS', dataIndex: 'sozlesme', width: 160, render: (v, k) => (k.hedef ? (
      <Space size={6}><Text strong={v >= k.hedef}>{v}/{k.hedef}</Text>
        <Progress percent={Math.min(100, Math.round((v / k.hedef) * 100))} size="small" showInfo={false} style={{ width: 70, margin: 0 }}
          status={v >= k.hedef ? 'success' : 'normal'} />{v >= k.hedef ? <CheckOutlined aria-label="hedef tuttu" /> : null}</Space>) : v) },
    { title: 'Görüşme', dataIndex: 'gorusme', width: 95, align: 'right' },
    { title: 'Lokasyona dönüşen', dataIndex: 'donusturulen', width: 130, align: 'right' },
    { title: 'Plan (yapıldı)', dataIndex: 'plan', width: 120, align: 'right', render: (v, k) => `${k.plan_yapildi}/${v}` },
  ], { search: false }), [erisim.kullanici_no]);

  const planKolonlari = useMemo(() => enhanceColumns([
    { title: 'Gün', dataIndex: 'gun', width: 130, render: (v) => `${gunAdi(v)} ${dayjs(v).format('DD.MM')}` },
    { title: 'Kişi', dataIndex: 'ad_soyad', width: 160 },
    { title: 'İl / İlçe', dataIndex: 'il_ilce', width: 150 },
    { title: 'Bölge', dataIndex: 'bolge', width: 170 },
    { title: 'Hedef lokasyon', dataIndex: 'hedef_lokasyon', width: 190 },
    { title: 'Not', dataIndex: 'plan_notu', width: 200 },
    { title: 'Durum', dataIndex: 'durum_adi', width: 100, render: (v, p) => <Tag color={PLAN_DURUM_RENK[p.durum]}>{v}</Tag> },
    { title: 'Görüşme', dataIndex: 'gorusme_adi', width: 170, render: (v, p) => (p.gorusme_id ? `#${p.gorusme_id} ${v || ''}` : '') },
    { title: '', key: 'x', width: 80, fixed: 'right', render: (_, p) => (planYazar(p) ? (
      <Space size={0}>
        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setPlan({ kayit: p })} aria-label="Düzenle" />
        <Popconfirm title="Plan satırı silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => planSil(p)}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Sil" />
        </Popconfirm>
      </Space>) : null) },
  ]), [erisim]); // eslint-disable-line react-hooks/exhaustive-deps

  const sozKolonlari = useMemo(() => enhanceColumns([
    { title: 'Giriş', dataIndex: 'eklenme', width: 100, render: tarih },
    { title: 'Söz. no', dataIndex: 'sozlesme_no', width: 100 },
    { title: 'Yer sahibi', dataIndex: 'ys_unvan', width: 200 },
    { title: 'Lokasyon', dataIndex: 'lokasyon_adi', width: 240, render: (v, s) => <LokasyonLinki id={s.lokasyon} ad={v} /> },
    { title: 'Kategori', dataIndex: 'kategori_adi', width: 150 },
    { title: 'Sorumlu', dataIndex: 'sorumlu_adi', width: 160, render: (v) => v || <Tag color="warning">Atanmamış</Tag> },
    { title: 'İmza', dataIndex: 'imza_adi', width: 150 },
    { title: 'Başlangıç', dataIndex: 'baslangic', width: 100, render: tarih },
    { title: 'Bitiş', dataIndex: 'bitis', width: 100, render: tarih },
    { title: 'Yıllık kira', dataIndex: 'yillik_kira', width: 130, align: 'right', render: (v, s) => (
      <Tooltip title={s.aylik ? `Aylık ${para(s.kira)} × 12` : null}><span className="nf-amount">{para(v)}</span></Tooltip>) },
    { title: 'Ekleyen', dataIndex: 'ekleyen_adi', width: 150 },
  ]), []);

  const trendKolonlari = useMemo(() => (veri ? [
    { title: 'Kişi', dataIndex: 'ad_soyad', width: 180, fixed: 'left', render: (v, k) => (
      <Space size={4}>{v}{k.kullanici_no && !k.uye ? <Tag>ekip dışı</Tag> : null}</Space>) },
    ...veri.trend.haftalar.map((h, i) => ({
      title: dayjs(h).format('DD.MM'), key: h, width: 64, align: 'right',
      render: (_, k) => {
        const n = k.degerler[i];
        return k.hedef && n >= k.hedef ? <Text type="success" strong>{n}</Text> : (n || <Text type="secondary">0</Text>);
      },
    })),
    { title: 'Toplam', key: 'top', width: 75, align: 'right', render: (_, k) => <Text strong>{k.degerler.reduce((a, b) => a + b, 0)}</Text> },
  ] : []), [veri]);

  const planSatirlari = useMemo(() => (veri?.planlar || []).map((p) => ({ ...p, il_ilce: [p.il_ad, p.ilce_adi].filter(Boolean).join(' / ') })), [veri]);

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<LeftOutlined />} onClick={() => kaydir(-1)} aria-label="Önceki hafta" />
        <DatePicker picker="week" allowClear={false} inputReadOnly value={dayjs(hafta)} style={{ width: 210 }}
          format={(v) => haftaEtiketi(pazartesi(v))} onChange={(v) => v && setHafta(pazartesi(v).format('YYYY-MM-DD'))} />
        <Button icon={<RightOutlined />} onClick={() => kaydir(1)} aria-label="Sonraki hafta" />
        <Button onClick={() => setHafta(pazartesi(dayjs()).format('YYYY-MM-DD'))}>Bu hafta</Button>
        <Button icon={<ReloadOutlined />} onClick={yukle} loading={yukleniyor}>Yenile</Button>
        {(erisim.uye || erisim.yonetici) && <Button type="primary" icon={<PlusOutlined />} onClick={() => setPlan({ kayit: null })}>Plan ekle</Button>}
        {erisim.yonetici && <Button icon={<TeamOutlined />} onClick={() => setEkipAcik(true)}>Ekip ve hedefler</Button>}
      </Space>

      {veri && !veri.ekip_toplam.uye && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Keşif ekibi henüz tanımlanmadı."
          description={erisim.yonetici ? '"Ekip ve hedefler" düğmesinden üyeleri ekleyin (öneri listesi otomatik eklenmez).'
            : 'Ekip tanımı Satış Süreci yazma yetkisi olanlarca yapılır.'} />
      )}

      {t && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6} xl={5}>
            <KpiKarti baslik="Yeni YS sözleşmesi (ekip)" deger={`${t.sozlesme} / ${t.hedef}`}>
              <Progress percent={t.hedef ? Math.min(100, Math.round((t.sozlesme / t.hedef) * 100)) : 0} size="small"
                status={t.hedef && t.sozlesme >= t.hedef ? 'success' : 'normal'} />
            </KpiKarti>
          </Col>
          <Col xs={12} md={6} xl={5}>
            <KpiKarti baslik="Tüm yeni YS sözleşmesi" deger={t.tum_sozlesme} alt={`ekip dışı ${t.ekip_disi} · atanmamış ${t.atanmamis}`} />
          </Col>
          <Col xs={8} md={4} xl={5}><KpiKarti baslik="Görüşme" deger={t.gorusme} /></Col>
          <Col xs={8} md={4} xl={4}><KpiKarti baslik="Lokasyona dönüşen" deger={t.donusturulen} /></Col>
          <Col xs={8} md={4} xl={5}><KpiKarti baslik="Planlanan saha günü" deger={t.plan} /></Col>
        </Row>
      )}

      <Card size="small" title={`Kişi bazında · ${haftaEtiketi(hafta)}`} style={{ marginBottom: 12 }}>
        <Table rowKey="kullanici_no" size="small" loading={yukleniyor} columns={kisiKolonlari} dataSource={veri?.kisiler || []}
          pagination={false} scroll={{ x: toplamGenislik(kisiKolonlari) }} locale={{ emptyText: <Empty description="Bu hafta kayıt yok" /> }} />
      </Card>

      <Card size="small" title="Saha planı" style={{ marginBottom: 12 }}>
        {mobil ? (
          <List dataSource={planSatirlari} locale={{ emptyText: 'Bu hafta plan yok' }} renderItem={(p) => (
            <List.Item actions={planYazar(p) ? [
              <Button key="d" size="small" type="text" icon={<EditOutlined />} onClick={() => setPlan({ kayit: p })} aria-label="Düzenle" />,
            ] : []}>
              <List.Item.Meta title={<Space>{`${gunAdi(p.gun)} ${dayjs(p.gun).format('DD.MM')}`}<Tag color={PLAN_DURUM_RENK[p.durum]}>{p.durum_adi}</Tag></Space>}
                description={<>{p.ad_soyad}{p.il_ilce ? ` · ${p.il_ilce}` : ''}{p.bolge ? ` · ${p.bolge}` : ''}
                  {p.hedef_lokasyon ? <div>{p.hedef_lokasyon}</div> : null}</>} />
            </List.Item>
          )} />
        ) : (
          <Table rowKey="id" size="small" loading={yukleniyor} columns={planKolonlari} dataSource={planSatirlari} pagination={false}
            scroll={{ x: toplamGenislik(planKolonlari) }} locale={{ emptyText: <Empty description="Bu hafta plan yok" /> }} />
        )}
      </Card>

      {veri && (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Trend trend={veri.trend} darkMode={darkMode} />
          <Table rowKey={(k) => k.kullanici_no || 'atanmamis'} size="small" columns={trendKolonlari} dataSource={veri.trend.kisiler}
            pagination={false} scroll={{ x: toplamGenislik(trendKolonlari) }} style={{ marginTop: 8 }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><Text strong>Toplam</Text></Table.Summary.Cell>
                {veri.trend.toplam.map((n, i) => <Table.Summary.Cell key={veri.trend.haftalar[i]} index={i + 1} align="right"><Text strong>{n}</Text></Table.Summary.Cell>)}
                <Table.Summary.Cell index={veri.trend.haftalar.length + 1} align="right">
                  <Text strong>{veri.trend.toplam.reduce((a, b) => a + b, 0)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )} />
        </Card>
      )}

      <Card size="small" title={`Sayılan yeni YS sözleşmeleri · ${haftaEtiketi(hafta)}`}
        extra={<Text type="secondary">m_sozlesme_tip 1 · imzalı (1/2/6) · sisteme giriş tarihine göre</Text>}>
        <Table rowKey="id" size="small" loading={yukleniyor} columns={sozKolonlari} dataSource={veri?.sozlesmeler || []} pagination={false}
          scroll={{ x: toplamGenislik(sozKolonlari) }} locale={{ emptyText: <Empty description="Bu hafta yeni YS sözleşmesi girilmedi" /> }} />
      </Card>

      {plan && (
        <PlanFormu api={api} secenek={secenek} erisim={erisim} hafta={hafta} kayit={plan.kayit}
          onKapat={() => setPlan(null)} onKaydedildi={() => yukle()} />
      )}
      {ekipAcik && (
        <EkipYonetimi api={api} secenek={secenek} onKapat={() => setEkipAcik(false)}
          onDegisti={() => { yukle(); if (onEkipDegisti) onEkipDegisti(); }} />
      )}
    </div>
  );
}
